const AUTH_STORAGE_KEY = import.meta.env.PUBLIC_AUTH_STORAGE_KEY;

if (!AUTH_STORAGE_KEY) {
  throw new Error('AUTH_STORAGE_KEY 환경변수가 설정되지 않았습니다.');
}

/**
 * 토큰 쌍 인터페이스
 */
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: string | number; // 만료 시간 (초) - 문자열 또는 숫자로 올 수 있음
  expiresAt?: number; // 만료 타임스탬프 (계산용)
}

/**
 * 비밀번호 변경 주기 상태 (ISMS-P 2.1.1).
 * 백엔드 PasswordChangeStatusDto 와 같은 모양이다.
 */
export type PasswordChangeState =
  | 'VALID'
  | 'CHANGE_ADVISED'
  | 'CHANGE_DUE'
  | 'CHANGE_FORCED';

export interface PasswordChangeStatus {
  state: PasswordChangeState;
  dueAt: string | null;
  forcedAt: string | null;
  daysUntilDue: number | null;
}

/**
 * 사용자 정보 인터페이스
 */
export interface User {
  id: string;
  email: string;
  name: string;
  lang: string;
  roles: string[];
  companyId: string;
  isRecordingConsented: boolean | null;
  /** 정책 적용 전 저장된 로그인 정보에는 없을 수 있다 */
  passwordChange?: PasswordChangeStatus;
}

/**
 * 인증 데이터 인터페이스 (토큰 + 사용자 정보)
 */
export interface AuthData {
  user: User;
  tokens: TokenPair;
}

/**
 * 인증 정보를 로컬 스토리지에 저장
 */
export function saveAuthData(authData: AuthData): void {
  try {
    if (!authData.tokens.accessToken || !authData.tokens.refreshToken) {
      console.error('Invalid tokens object:', authData.tokens);
      throw new Error('Invalid tokens provided');
    }

    // expiresIn이 문자열인 경우 숫자로 변환
    const expiresIn =
      typeof authData.tokens.expiresIn === 'string'
        ? Number.parseInt(authData.tokens.expiresIn, 10)
        : authData.tokens.expiresIn;

    const expiresAt = Date.now() + expiresIn * 1000;

    const dataToSave = {
      user: authData.user,
      tokens: {
        ...authData.tokens,
        expiresIn,
        expiresAt,
      },
    };

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Failed to save auth data:', error);
    throw error;
  }
}

/**
 * 토큰만 저장하는 함수 (이전 버전과의 호환성 유지)
 */
export function saveTokens(tokens: TokenPair): void {
  // 사용자 정보 없이 토큰만 저장 (이전 로직 유지)
  try {
    if (!tokens.accessToken || !tokens.refreshToken) {
      console.error('Invalid tokens object:', tokens);
      throw new Error('Invalid tokens provided');
    }

    // 기존에 저장된 데이터가 있는지 확인
    const existingData = getAuthData();
    const user = existingData?.user || {
      id: '',
      email: '',
      name: '',
      lang: '',
      roles: [],
      companyId: '',
      isRecordingConsented: null,
    };

    // 새로운 전체 인증 데이터 저장
    saveAuthData({
      user,
      tokens,
    });
  } catch (error) {
    console.error('Failed to save tokens:', error);
    throw error;
  }
}

/**
 * 로컬 스토리지에서 인증 정보 가져오기
 */
export function getAuthData(): AuthData | null {
  try {
    const dataStr = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!dataStr) {
      return null;
    }

    const authData = JSON.parse(dataStr) as AuthData;
    return authData;
  } catch (error) {
    console.error('Failed to parse auth data:', error);
    return null;
  }
}

/**
 * 로컬 스토리지에서 토큰 정보만 가져오기
 */
export function getTokens(): TokenPair | null {
  const authData = getAuthData();
  return authData?.tokens || null;
}

/**
 * 로컬 스토리지에서 사용자 정보만 가져오기
 */
export function getUser(): User | null {
  const authData = getAuthData();
  return authData?.user || null;
}

/**
 * 액세스 토큰만 가져오기
 */
export function getAccessToken(): string | null {
  const tokens = getTokens();
  return tokens?.accessToken || null;
}

/**
 * 리프레시 토큰만 가져오기
 */
export function getRefreshToken(): string | null {
  const tokens = getTokens();
  return tokens?.refreshToken || null;
}

/**
 * 토큰이 만료되었는지 확인
 */
export function isTokenExpired(): boolean {
  const tokens = getTokens();
  if (!tokens?.expiresAt) {
    return true;
  }

  // 만료 10초 전에 만료된 것으로 간주 (안전 마진)
  const isExpired = tokens.expiresAt < Date.now() + 10000;

  return isExpired;
}

/**
 * 인증 정보 초기화 (로그아웃)
 */
export function clearTokens(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  clearPasswordChangeNotices();
}

/**
 * 비밀번호 변경 안내를 "이번에는 그만 보기"로 닫아둔 기록.
 *
 * sessionStorage 라 탭이 살아 있으면 로그아웃·재로그인을 해도 남는다. 그대로 두면
 * 같은 탭에서 다른 사람이 로그인했을 때 그 사람에게도 안내가 뜨지 않으므로,
 * 로그아웃 시 함께 비운다.
 */
export const PASSWORD_CHANGE_POSTPONE_KEY = 'passwordChange.postponed';
export const PASSWORD_CHANGE_NOTICE_DISMISSED_KEY =
  'passwordChange.noticeDismissed';

export function clearPasswordChangeNotices(): void {
  try {
    sessionStorage.removeItem(PASSWORD_CHANGE_POSTPONE_KEY);
    sessionStorage.removeItem(PASSWORD_CHANGE_NOTICE_DISMISSED_KEY);
  } catch {
    // 저장이 막힌 환경에서는 애초에 기록이 남지 않는다
  }
}

/**
 * 액세스 토큰 갱신 (리프레시 토큰 사용)
 * 실제 API 호출은 별도 함수로 수행
 */
export async function refreshAccessToken(): Promise<TokenPair> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Refresh token not found');
  }

  try {
    // 여기서는 실제 구현을 생략하고, auth-service.ts에서 구현
    throw new Error('This function should be implemented in auth-service.ts');
  } catch (error) {
    clearTokens(); // 오류 발생 시 토큰 클리어
    throw error;
  }
}
